import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Download, DatabaseBackup, Upload, ShieldAlert, Power, ToggleLeft,
  FileDown, Trash2, UserX, FileJson, Users as UsersIcon, CheckCircle2,
  XCircle, Calculator, Plus, KeyRound, Ban, FileUp, Search, CheckSquare, Square,
} from "lucide-react";
import api, { formatApiError, API, getAuthToken } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useTableTools } from "@/lib/useTableTools";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useI18n } from "@/context/I18nContext";

const ROLES = ["admin", "support", "partner", "driver", "client"];

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <h3 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">{Icon && <Icon size={18} />} {title}</h3>
      {children}
    </div>
  );
}

function Row({ l, v, bold }) {
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 ${bold ? "font-semibold text-slate-900 bg-slate-50" : "text-slate-600"}`}>
      <span>{l}</span><span>{v}</span>
    </div>
  );
}

export default function System() {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const [settings, setSettings] = useState(null);
  const [backups, setBackups] = useState([]);
  const [users, setUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [audit, setAudit] = useState(null);
  const [auditDist, setAuditDist] = useState(500);
  const [auditCode, setAuditCode] = useState("");
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "support" });
  const [gdprUser, setGdprUser] = useState("");
  const [busy, setBusy] = useState(false);
  const restoreRef = useRef(null);
  const importRef = useRef(null);
  const token = getAuthToken();

  const load = () => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/admin/backups").then(({ data }) => setBackups(data)).catch(() => {});
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/admin/users").then(({ data }) => setAdminUsers(data)).catch(() => {});
    api.get("/admin/launch-checklist").then(({ data }) => setChecklist(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const loadAudit = (code, dist) => {
    const c = code ?? auditCode; const d = dist ?? auditDist;
    api.get(`/admin/pricing-audit?distance_km=${d}${c ? `&transport_code=${c}` : ""}`)
      .then(({ data }) => { setAudit(data); if (!auditCode && data.sample_input?.transport_code) setAuditCode(data.sample_input.transport_code); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };

  const createUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) { toast.error("Compila tutti i campi"); return; }
    try { await api.post("/admin/users", newUser); toast.success("Utente creato"); setNewUser({ name: "", email: "", password: "", role: "support" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setUserRole = async (id, role) => {
    try { await api.patch(`/admin/users/${id}/role`, { role }); toast.success("Ruolo aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setUserStatus = async (id, status) => {
    try { await api.patch(`/admin/users/${id}/status`, { status }); toast.success("Stato aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resetUserPassword = async (id) => {
    const pw = window.prompt("Nuova password (min 6 caratteri):");
    if (!pw) return;
    try { await api.post(`/admin/users/${id}/password`, { password: pw }); toast.success("Password aggiornata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteUser = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo utente?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Utente eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const userTools = useTableTools(adminUsers, ["name", "email", "role", "status"]);
  const bulkUsers = async (action) => {
    const ids = [...userTools.selected].filter((id) => id !== me?.id);
    if (!ids.length) { toast.error(t("common.none")); return; }
    if (action.kind === "delete" && !window.confirm(`${t("bulk.delete")} ${ids.length}?`)) return;
    let role = null;
    if (action.kind === "role") { role = window.prompt("admin / support / partner / driver / client"); if (!role) return; }
    try {
      for (const id of ids) {
        if (action.kind === "delete") await api.delete(`/admin/users/${id}`);
        else if (action.kind === "activate") await api.patch(`/admin/users/${id}/status`, { status: "active" });
        else if (action.kind === "suspend") await api.patch(`/admin/users/${id}/status`, { status: "suspended" });
        else if (action.kind === "role") await api.patch(`/admin/users/${id}/role`, { role });
      }
      toast.success("✓"); userTools.clear(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportSettings = () => {
    fetch(`${API}/admin/settings/export`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob()).then((bl) => { const u = URL.createObjectURL(bl); const a = document.createElement("a"); a.href = u; a.download = "rimpatrio24-settings.json"; a.click(); toast.success("Impostazioni esportate"); });
  };
  const importSettings = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!window.confirm("Importare le impostazioni? I valori attuali verranno sovrascritti.")) return;
    try {
      const text = await file.text();
      await api.post("/admin/settings/import", { data: JSON.parse(text) });
      toast.success("Impostazioni importate"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "File non valido"); }
    finally { if (importRef.current) importRef.current.value = ""; }
  };

  const generateDemo = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/demo-data"); toast.success(`${t("toast.demoCreated")} (${Object.values(data.created).reduce((a, b) => a + b, 0)})`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const clearDemo = async () => {
    if (!window.confirm(t("sys.demoConfirm"))) return;
    setBusy(true);
    try { await api.delete("/admin/demo-data"); toast.success(t("toast.demoCleared")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const flags = settings?.feature_flags || {};
  const setFlag = async (key, val) => {
    const next = structuredClone(settings);
    (next.feature_flags ||= {})[key] = val;
    setSettings(next);
    try { await api.put("/settings", { data: { feature_flags: next.feature_flags } }); toast.success("Aggiornato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setPlan = (idx, field, val) => {
    const next = structuredClone(settings);
    next.subscription_plans[idx][field] = val;
    setSettings(next);
  };
  const savePlans = async () => {
    try { await api.put("/settings", { data: { subscription_plans: settings.subscription_plans } }); toast.success("Piani salvati"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createBackup = async () => {
    setBusy(true);
    try { await api.post("/admin/backup"); toast.success("Backup creato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const restore = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!window.confirm("Ripristinare il backup? I dati attuali verranno sovrascritti.")) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { const { data } = await api.post("/admin/restore", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Ripristino completato (${data.files} file)`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); if (restoreRef.current) restoreRef.current.value = ""; }
  };

  const gdpr = async (action) => {
    if (!gdprUser) { toast.error("Seleziona un utente"); return; }
    if (action !== "export" && !window.confirm(`Confermi l'azione GDPR: ${action}?`)) return;
    try {
      if (action === "export") {
        const { data } = await api.get(`/admin/gdpr/export/${gdprUser}`);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `gdpr-${gdprUser}.json`; a.click();
        toast.success("Dati esportati");
      } else if (action === "anonymize") {
        await api.post(`/admin/gdpr/anonymize/${gdprUser}`); toast.success("Utente anonimizzato"); load();
      } else if (action === "delete") {
        await api.delete(`/admin/gdpr/delete/${gdprUser}`); toast.success("Utente eliminato"); load();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!settings) return <div className="text-slate-400">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="system-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{t("menu.system")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("sys.subtitle")}</p>
      </div>

      <Tabs defaultValue="ops" onValueChange={(v) => { if (v === "pricing" && !audit) loadAudit(); }}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="ops" data-testid="sys-tab-ops">{t("sys.t.ops")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="sys-tab-users">{t("sys.t.users")}</TabsTrigger>
          <TabsTrigger value="launch" data-testid="sys-tab-launch">{t("sys.t.launch")}</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="sys-tab-pricing">{t("sys.t.pricing")}</TabsTrigger>
          <TabsTrigger value="backup" data-testid="sys-tab-backup">{t("sys.t.backup")}</TabsTrigger>
          <TabsTrigger value="gdpr" data-testid="sys-tab-gdpr">{t("sys.t.gdpr")}</TabsTrigger>
          <TabsTrigger value="plans" data-testid="sys-tab-plans">{t("sys.t.plans")}</TabsTrigger>
        </TabsList>

        <TabsContent value="ops" className="mt-6 space-y-6">
          <Card title={t("sys.c.maintenance")} icon={Power}>
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500 max-w-md">{t("sys.maintenanceDesc")}</p>
              <Switch checked={!!flags.maintenance_mode} onCheckedChange={(v) => setFlag("maintenance_mode", v)} data-testid="maintenance-toggle" />
            </div>
          </Card>
          <Card title={t("sys.demoTitle")} icon={DatabaseBackup}>
            <p className="text-sm text-slate-500 mb-4">{t("sys.demoDesc")}</p>
            <div className="flex gap-3 flex-wrap">
              <Button onClick={generateDemo} disabled={busy} className="bg-blue-900 hover:bg-blue-800" data-testid="demo-generate-btn"><Plus size={16} className="mr-1.5" /> {t("sys.generateDemo")}</Button>
              <Button variant="outline" onClick={clearDemo} disabled={busy} data-testid="demo-clear-btn"><Trash2 size={16} className="mr-1.5" /> {t("sys.clearDemo")}</Button>
            </div>
          </Card>
          <Card title={t("sys.c.exportImport")} icon={FileJson}>
            <p className="text-sm text-slate-500 mb-4">{t("sys.exportImportDesc")}</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={exportSettings} data-testid="settings-export-btn"><FileDown size={16} className="mr-1.5" /> {t("sys.exportJson")}</Button>
              <input ref={importRef} type="file" accept="application/json,.json" className="hidden" onChange={importSettings} data-testid="settings-import-input" />
              <Button variant="outline" onClick={() => importRef.current?.click()} data-testid="settings-import-btn"><FileUp size={16} className="mr-1.5" /> {t("sys.importJson")}</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-6 space-y-6">
          <Card title="Nuovo utente" icon={Plus}>
            <div className="grid md:grid-cols-5 gap-3 items-end">
              <div className="space-y-1.5"><Label>Nome</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="newuser-name" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="newuser-email" /></div>
              <div className="space-y-1.5"><Label>Password</Label><Input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} data-testid="newuser-password" /></div>
              <div className="space-y-1.5"><Label>Ruolo</Label>
                <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} data-testid="newuser-role">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select></div>
              <Button onClick={createUser} className="bg-blue-900 hover:bg-blue-800" data-testid="newuser-create-btn"><Plus size={16} className="mr-1.5" /> Crea</Button>
            </div>
          </Card>
          <Card title={`${t("sys.t.users")} (${adminUsers.length})`} icon={UsersIcon}>
            <div className="flex items-center gap-2 flex-wrap mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={userTools.query} onChange={(e) => userTools.setQuery(e.target.value)} placeholder={t("common.search")} className="pl-9" data-testid="users-search" />
              </div>
              <Button variant="outline" size="sm" onClick={userTools.selectAllVisible} data-testid="users-select-all"><CheckSquare size={15} className="mr-1.5" /> {t("common.selectAll")}</Button>
              <Button variant="outline" size="sm" onClick={userTools.clear} data-testid="users-clear"><Square size={15} className="mr-1.5" /> {t("common.deselect")}</Button>
            </div>
            {userTools.selected.size > 0 && (
              <div className="flex items-center gap-2 flex-wrap bg-blue-900 text-white rounded-xl px-4 py-2.5 mb-4" data-testid="users-bulk-bar">
                <span className="text-sm font-medium">{userTools.selected.size} {t("common.selected")}</span>
                <div className="h-5 w-px bg-white/20 mx-1" />
                <Button size="sm" variant="secondary" className="h-8" onClick={() => bulkUsers({ kind: "activate" })} data-testid="users-bulk-activate"><CheckCircle2 size={14} className="mr-1" /> {t("status.active")}</Button>
                <Button size="sm" variant="secondary" className="h-8" onClick={() => bulkUsers({ kind: "suspend" })} data-testid="users-bulk-suspend"><Ban size={14} className="mr-1" /> {t("bulk.suspend")}</Button>
                <Button size="sm" variant="secondary" className="h-8" onClick={() => bulkUsers({ kind: "role" })} data-testid="users-bulk-role"><UsersIcon size={14} className="mr-1" /> {t("common.role")}</Button>
                <Button size="sm" variant="destructive" className="h-8" onClick={() => bulkUsers({ kind: "delete" })} data-testid="users-bulk-delete"><Trash2 size={14} className="mr-1" /> {t("bulk.delete")}</Button>
                <Button size="sm" variant="ghost" className="h-8 ml-auto text-white hover:bg-white/10" onClick={userTools.clear}>{t("common.cancelSelection")}</Button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase text-slate-400">
                  <th className="w-8"></th><th className="py-2">{t("fld.name")}</th><th>{t("auth.email")}</th><th>{t("common.role")}</th><th>{t("common.status")}</th><th>{t("trk.updated")}</th><th className="text-right">{t("common.actions")}</th>
                </tr></thead>
                <tbody>
                  {userTools.filtered.map((u) => (
                    <tr key={u.id} className="border-t border-slate-100" data-testid="user-row">
                      <td><Checkbox checked={userTools.selected.has(u.id)} onCheckedChange={() => userTools.toggle(u.id)} disabled={u.id === me?.id} data-testid={`user-check-${u.id}`} /></td>
                      <td className="py-2 font-medium text-slate-700">{u.name}</td>
                      <td className="text-slate-500">{u.email}</td>
                      <td>
                        <select className="h-8 rounded-md border border-slate-200 px-2 text-xs bg-white" value={u.role} onChange={(e) => setUserRole(u.id, e.target.value)} data-testid={`user-role-${u.id}`}>
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td>
                        <select className="h-8 rounded-md border border-slate-200 px-2 text-xs bg-white" value={u.status || "active"} onChange={(e) => setUserStatus(u.id, e.target.value)} data-testid={`user-status-${u.id}`}>
                          {["active", "suspended", "inactive"].map((st) => <option key={st} value={st}>{st}</option>)}
                        </select>
                      </td>
                      <td className="text-xs text-slate-400">{u.last_login ? formatDateTime(u.last_login) : "—"}</td>
                      <td className="text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" title="Reset password" onClick={() => resetUserPassword(u.id)} data-testid={`user-pwd-${u.id}`}><KeyRound size={15} /></Button>
                        {u.id !== me?.id && <Button size="icon" variant="ghost" className="text-rose-600" title="Elimina" onClick={() => deleteUser(u.id)} data-testid={`user-delete-${u.id}`}><Trash2 size={15} /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="launch" className="mt-6">
          <Card title="Checklist di avvio" icon={CheckCircle2}>
            {!checklist ? <p className="text-sm text-slate-400">{t("common.loading")}</p> : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <span className={`text-sm font-semibold ${checklist.ready ? "text-emerald-600" : "text-amber-600"}`} data-testid="launch-summary">{checklist.ok}/{checklist.total} completati</span>
                  {checklist.ready && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Pronto al lancio</span>}
                </div>
                <div className="space-y-2">
                  {checklist.checks.map((c) => (
                    <div key={c.key} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3" data-testid={`check-${c.key}`}>
                      {c.ok ? <CheckCircle2 size={18} className="text-emerald-500 mt-0.5" /> : <XCircle size={18} className="text-rose-400 mt-0.5" />}
                      <div>
                        <div className="text-sm font-medium text-slate-700">{c.label}</div>
                        {!c.ok && <div className="text-xs text-slate-400 mt-0.5">{c.hint}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="pricing" className="mt-6">
          <Card title="Audit prezzi — fonte dei calcoli" icon={Calculator}>
            <p className="text-sm text-slate-500 mb-4">Trasparenza sul motore di pricing: <code className="text-xs">km fatturati = distanza × moltiplicatore</code>, <code className="text-xs">costo = base + €/km × km fatturati</code>, poi servizi e IVA.</p>
            {!audit ? <p className="text-sm text-slate-400">{t("common.loading")}</p> : (
              <>
                <div className="grid sm:grid-cols-4 gap-3 mb-5 text-sm">
                  <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Moltiplicatore A/R</div><div className="font-semibold">{audit.rules.return_multiplier}</div></div>
                  <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">IVA %</div><div className="font-semibold">{audit.rules.vat_pct}%</div></div>
                  <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Commissione partner</div><div className="font-semibold">{audit.rules.partner_commission_pct}%</div></div>
                  <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-400">Valuta</div><div className="font-semibold">{audit.rules.currency}</div></div>
                </div>
                <div className="flex items-end gap-3 mb-4">
                  <div className="space-y-1.5"><Label>Trasporto</Label>
                    <select className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={auditCode} onChange={(e) => { setAuditCode(e.target.value); loadAudit(e.target.value, auditDist); }} data-testid="audit-transport">
                      {audit.transport_types.map((t) => <option key={t.code} value={t.code}>{t.name || t.code}</option>)}
                    </select></div>
                  <div className="space-y-1.5"><Label>Distanza (km)</Label><Input type="number" className="w-32" value={auditDist} onChange={(e) => setAuditDist(Number(e.target.value))} onBlur={() => loadAudit(auditCode, auditDist)} data-testid="audit-distance" /></div>
                  <Button variant="outline" onClick={() => loadAudit(auditCode, auditDist)} data-testid="audit-recalc">Ricalcola</Button>
                </div>
                {audit.sample && !audit.sample.error && (
                  <div className="rounded-lg border border-slate-200 divide-y text-sm" data-testid="audit-breakdown">
                    <Row l={`Distanza stradale`} v={`${audit.sample.road_distance_km} km`} />
                    <Row l={`Km fatturati (× ${audit.sample.return_multiplier})`} v={`${audit.sample.billable_km} km`} />
                    <Row l={`Costo trasporto (${audit.sample.transport.base_price} + ${audit.sample.transport.price_per_km}/km)`} v={`${audit.sample.transport.cost} ${audit.rules.currency}`} />
                    <Row l="Subtotale" v={`${audit.sample.subtotal} ${audit.rules.currency}`} />
                    <Row l={`IVA (${audit.sample.vat_pct}%)`} v={`${audit.sample.tax} ${audit.rules.currency}`} />
                    <Row l="Totale" v={`${audit.sample.total} ${audit.rules.currency}`} bold />
                  </div>
                )}
              </>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="backup" className="mt-6 space-y-6">
          <Card title="Backup & Ripristino" icon={DatabaseBackup}>
            <p className="text-sm text-slate-500 mb-4">Il backup include database (ordini, fatture, documenti, impostazioni, template, utenti) e i file/PDF archiviati.</p>
            <div className="flex gap-3">
              <Button onClick={createBackup} disabled={busy} className="bg-blue-900 hover:bg-blue-800" data-testid="backup-create-btn"><DatabaseBackup size={16} className="mr-1.5" /> {busy ? "Attendere…" : "Crea backup"}</Button>
              <input ref={restoreRef} type="file" accept=".zip" className="hidden" onChange={restore} data-testid="restore-input" />
              <Button variant="outline" onClick={() => restoreRef.current?.click()} disabled={busy} data-testid="restore-btn"><Upload size={16} className="mr-1.5" /> Ripristina</Button>
            </div>
            <div className="mt-5 space-y-2">
              {backups.length === 0 && <p className="text-sm text-slate-400">Nessun backup.</p>}
              {backups.map((b) => (
                <div key={b.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm" data-testid="backup-row">
                  <FileJson size={16} className="text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-700 truncate">{b.filename}</div>
                    <div className="text-xs text-slate-400">{(b.size / 1024).toFixed(0)} KB · {b.files} file · {formatDateTime(b.created_at)}</div>
                  </div>
                  <a href={`${API}/admin/backup/${b.id}/download`} onClick={(e) => { e.preventDefault(); fetch(`${API}/admin/backup/${b.id}/download`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} }).then((r) => r.blob()).then((bl) => { const u = URL.createObjectURL(bl); const a = document.createElement("a"); a.href = u; a.download = b.filename; a.click(); }); }}>
                    <Button size="sm" variant="ghost" data-testid="backup-download-btn"><Download size={15} /></Button>
                  </a>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="gdpr" className="mt-6">
          <Card title="Strumenti GDPR" icon={ShieldAlert}>
            <p className="text-sm text-slate-500 mb-4">Esporta, anonimizza o elimina i dati di un utente. Ogni azione è registrata nel log di audit.</p>
            <div className="space-y-1.5 max-w-md">
              <Label>Utente</Label>
              <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={gdprUser} onChange={(e) => setGdprUser(e.target.value)} data-testid="gdpr-user-select">
                <option value="">Seleziona…</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email} ({u.role})</option>)}
              </select>
            </div>
            <div className="flex gap-3 mt-4">
              <Button variant="outline" onClick={() => gdpr("export")} data-testid="gdpr-export-btn"><FileDown size={16} className="mr-1.5" /> Esporta</Button>
              <Button variant="outline" onClick={() => gdpr("anonymize")} data-testid="gdpr-anonymize-btn"><UserX size={16} className="mr-1.5" /> Anonimizza</Button>
              <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => gdpr("delete")} data-testid="gdpr-delete-btn"><Trash2 size={16} className="mr-1.5" /> Elimina</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="plans" className="mt-6 space-y-6">
          <Card title="Funzionalità (feature flags)" icon={ToggleLeft}>
            <div className="space-y-3">
              {[["marketplace_enabled", "Marketplace attivo"], ["subscriptions_enabled", "Abbonamenti attivi"]].map(([k, lbl]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{lbl}</span>
                  <Switch checked={!!flags[k]} onCheckedChange={(v) => setFlag(k, v)} data-testid={`flag-${k}`} />
                </div>
              ))}
            </div>
          </Card>
          <Card title="Piani di abbonamento partner">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase text-slate-400">
                  <th className="py-2">Piano</th><th>€/mese</th><th>€/anno</th><th>Marketplace</th><th>Statistiche</th><th>Priorità</th>
                </tr></thead>
                <tbody>
                  {settings.subscription_plans?.map((p, i) => (
                    <tr key={p.code} className="border-t border-slate-100" data-testid="plan-row">
                      <td className="py-2 font-medium">{p.name}</td>
                      <td><Input type="number" className="w-20 h-8" value={p.monthly_price} onChange={(e) => setPlan(i, "monthly_price", Number(e.target.value))} data-testid={`plan-${p.code}-monthly`} /></td>
                      <td><Input type="number" className="w-24 h-8" value={p.yearly_price} onChange={(e) => setPlan(i, "yearly_price", Number(e.target.value))} /></td>
                      <td><Switch checked={!!p.marketplace_access} onCheckedChange={(v) => setPlan(i, "marketplace_access", v)} /></td>
                      <td><Switch checked={!!p.statistics_access} onCheckedChange={(v) => setPlan(i, "statistics_access", v)} /></td>
                      <td><Switch checked={!!p.priority_access} onCheckedChange={(v) => setPlan(i, "priority_access", v)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={savePlans} className="bg-blue-900 hover:bg-blue-800 mt-4" data-testid="plans-save-btn">Salva piani</Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
